"use client";

import { useAuth } from "@/contexts/AuthContext";
import { usePathname } from "next/navigation";
import { useRouteAccessGuard } from "@/lib/hooks/useRouteAccessGuard";
import { buildLoginHref } from "@/lib/navigation/authRedirect";

interface RouteGuardProps {
  children: React.ReactNode;
  /** Login (or other) base path; full return URL is appended as redirectTo. */
  redirectTo?: string;
}

function currentReturnPath(pathname: string | null): string {
  if (typeof window !== "undefined") {
    const path = window.location.pathname || pathname || "/";
    const search = window.location.search || "";
    return `${path}${search}`;
  }
  return pathname || "/";
}

/**
 * Auth gate for protected routes.
 * Preserves pathname + query (e.g. /sell?fromRequest=…) on login return (P2.8).
 */
export default function RouteGuard({
  children,
  redirectTo = "/login",
}: RouteGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const pathname = usePathname();
  const returnPath = currentReturnPath(pathname);

  const loginHref =
    redirectTo === "/login"
      ? buildLoginHref(returnPath)
      : `${redirectTo}?redirectTo=${encodeURIComponent(returnPath)}`;

  const guard = useRouteAccessGuard({
    isLoading,
    canAccess: isAuthenticated,
    redirectTo: loginHref,
    redirectMethod: "replace",
  });

  if (guard.isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-foreground"></div>
      </div>
    );
  }

  return guard.canRender ? <>{children}</> : null;
}
